/**
 * Dashboard page component.
 * Displays an overview of disk images and sandboxes.
 *
 * @module pages/DashboardPage
 */

import { Link } from 'react-router-dom';
import { HardDrive, Box, Plus, ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Spinner,
  Badge,
  getDiskImageStateBadgeVariant,
} from '@/components/ui';
import { useDiskImages, useSandboxes } from '@/hooks';

/**
 * Dashboard page showing resource overview
 */
export function DashboardPage() {
  const { data: diskImages, isLoading: diskImagesLoading } = useDiskImages();
  const { data: sandboxes, isLoading: sandboxesLoading } = useSandboxes();

  const stats = [
    {
      name: 'Total Disk Images',
      value: diskImages?.length ?? 0,
      icon: <HardDrive className="h-6 w-6" />,
      href: '/disk-images',
      color: 'bg-blue-500',
    },
    {
      name: 'Total Sandboxes',
      value: sandboxes?.length ?? 0,
      icon: <Box className="h-6 w-6" />,
      href: '/sandboxes',
      color: 'bg-purple-500',
    },
    {
      name: 'Ready Images',
      value: diskImages?.filter((d) => d.status?.state === 'Ready').length ?? 0,
      icon: <CheckCircle2 className="h-6 w-6" />,
      href: '/disk-images',
      color: 'bg-green-500',
    },
    {
      name: 'Creating/Pending',
      value:
        (diskImages?.filter((d) => d.status?.state === 'Creating' || d.status?.state === 'Pending')
          .length ?? 0) + (sandboxes?.filter((s) => s.id?.startsWith('pending')).length ?? 0),
      icon: <Clock className="h-6 w-6" />,
      href: '/disk-images',
      color: 'bg-yellow-500',
    },
  ];

  return (
    <div>
      <PageHeader title="Dashboard" description="Overview of your disk images and sandboxes" />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <Link key={stat.name} to={stat.href}>
            <Card className="hover:shadow-md transition-shadow">
              <CardBody>
                <div className="flex items-center gap-4">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-lg ${stat.color} text-white`}
                  >
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{stat.name}</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-200">
                      {diskImagesLoading || sandboxesLoading ? <Spinner size="sm" /> : stat.value}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Disk Images */}
        <Card>
          <CardHeader
            title="Recent Disk Images"
            action={
              <Link to="/disk-images">
                <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="h-4 w-4" />}>
                  View All
                </Button>
              </Link>
            }
          />
          <CardBody noPadding>
            {diskImagesLoading ? (
              <div className="p-6 flex justify-center">
                <Spinner />
              </div>
            ) : diskImages && diskImages.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {diskImages.slice(0, 5).map((image) => (
                  <Link
                    key={image.id}
                    to={`/disk-images/${encodeURIComponent(image.id)}`}
                    className="flex items-center justify-between px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <HardDrive className="h-4 w-4 text-gray-400" />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                          {image.labels?.['name'] || image.id}
                        </span>
                        {image.labels?.['name'] && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            {image.id}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge
                      variant={getDiskImageStateBadgeVariant(image.status?.state || 'Unknown')}
                    >
                      {image.status?.state || 'Unknown'}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No disk images yet
              </div>
            )}
          </CardBody>
        </Card>

        {/* Recent Sandboxes */}
        <Card>
          <CardHeader
            title="Recent Sandboxes"
            action={
              <Link to="/sandboxes">
                <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="h-4 w-4" />}>
                  View All
                </Button>
              </Link>
            }
          />
          <CardBody noPadding>
            {sandboxesLoading ? (
              <div className="p-6 flex justify-center">
                <Spinner />
              </div>
            ) : sandboxes && sandboxes.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {sandboxes.slice(0, 5).map((sandbox) => (
                  <Link
                    key={sandbox.id}
                    to={`/sandboxes/${encodeURIComponent(sandbox.id)}`}
                    className="flex items-center justify-between px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Box className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                        {sandbox.id}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {sandbox.resources?.cpu} / {sandbox.resources?.memory}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No sandboxes yet
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader title="Quick Actions" />
        <CardBody>
          <div className="flex flex-wrap gap-4">
            <Link to="/disk-images/new">
              <Button leftIcon={<Plus className="h-4 w-4" />}>Create Disk Image</Button>
            </Link>
            <Link to="/sandboxes/new">
              <Button variant="secondary" leftIcon={<Plus className="h-4 w-4" />}>
                Create Sandbox
              </Button>
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
